/**
 * 资金托管 · 卖家提现（N18 / PRD §5.6.3 卖家出金）
 *
 * 后端路由（router.go / handler/escrow_withdraw.go）：
 *   GET  /api/v1/escrow/account              → 我的卖家托管账户余额（分）
 *   POST /api/v1/escrow/withdrawals          → 申请提现（需支付密码 2FA）
 *   GET  /api/v1/escrow/withdrawals/mine     → 我的提现单
 *
 * 金额口径：后端一律 int64「分」存储与传输，前端展示时除以 100 转元，
 * 避免浮点误差（对齐 escrow 全链路约定）。
 *
 * 错误码（service/escrow_withdraw.go 4701-4705 + payment_2fa.go 2004-2007）：
 *   4701 金额不合法 / 4702 余额不足 / 4703 收款账户不合法 / 4704 提现单不存在 / 4705 状态不允许
 *   2004 未设置支付密码 / 2005 资金操作必传 / 2006 支付密码错误 / 2007 连续失败锁定
 */
import { get, post } from './http';

/** 起提金额（分），与后端 service.WithdrawMinCents 对齐 */
export const WITHDRAW_MIN_CENTS = 100;

/** 提现单状态（与后端 model.WithdrawStatusXxx 对齐） */
export const WITHDRAW_STATUS = {
  PENDING: 0, // 待打款
  PAID: 1, // 已打款
  REJECTED: 2, // 已驳回（资金已退回余额）
} as const;

/** 提现单状态中文名 */
export const WITHDRAW_STATUS_LABEL: Record<number, string> = {
  0: '待打款',
  1: '已打款',
  2: '已驳回',
};

/** 卖家托管账户余额视图（分） */
export interface EscrowBalance {
  user_id: number;
  account_type: number;
  /** 可用余额（分） */
  balance: number;
  /** 冻结金额（分） */
  frozen: number;
  /** 可提现金额（分） */
  withdrawable: number;
  /** 待打款提现单占用金额合计（分，信息展示用） */
  pending_cents: number;
}

/** 提现单 */
export interface EscrowWithdrawal {
  id: number;
  withdraw_no: string;
  user_id: number;
  account_type: number;
  amount_cents: number;
  fee_cents: number;
  actual_cents: number;
  bank_name: string;
  /** 后端已脱敏/原样返回，前端展示时建议再行掩码 */
  bank_account: string;
  account_name: string;
  /** 0-待打款 1-已打款 2-已驳回 */
  status: number;
  reject_reason: string;
  reviewer_id: number;
  reviewer_name: string;
  reviewed_at: string | null;
  created_at: string;
  updated_at: string;
}

/** 提现申请参数 */
export interface CreateWithdrawalParams {
  /** 提现金额（分），避免浮点误差 */
  amount_cents: number;
  /** 支付密码（资金出账必传 2FA） */
  payment_password: string;
  bank_name: string;
  bank_account: string;
  account_name: string;
}

/** 查询我的卖家托管账户余额 */
export async function getEscrowAccount(): Promise<EscrowBalance> {
  return get<EscrowBalance>('/escrow/account');
}

/** 申请提现（后端校验支付密码 → 行锁扣减余额 → 生成待打款提现单） */
export async function createWithdrawal(params: CreateWithdrawalParams): Promise<EscrowWithdrawal> {
  return post<EscrowWithdrawal>('/escrow/withdrawals', params);
}

/** 我的提现单（倒序，最多 200 条） */
export async function listMyWithdrawals(): Promise<EscrowWithdrawal[]> {
  return get<EscrowWithdrawal[]>('/escrow/withdrawals/mine');
}

/** 分 → 元（展示用，保留 2 位小数） */
export function centsToYuan(cents: number | null | undefined): number {
  return Number(cents ?? 0) / 100;
}

/** 银行卡号掩码：保留前 4 后 4 */
export function maskBankAccount(value: string | null | undefined): string {
  if (!value) return '-';
  const s = String(value);
  if (s.length <= 8) return s;
  return `${s.slice(0, 4)}****${s.slice(-4)}`;
}
